import { AlertTriangle, ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { Link } from 'react-router-dom';
import remarkGfm from 'remark-gfm';

import { RapidSOSMark } from '@/components/RapidSOSMark/RapidSOSMark';
import { RapidSOSWordmark } from '@/components/RapidSOSMark/RapidSOSWordmark';
import { type LegalDocument } from '@/legal/documents';
import { LegalLinks } from '@/legal/LegalLinks';

function formatEffectiveDate(isoDate: string, language: string): string {
  const date = new Date(`${isoDate}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return isoDate;
  return new Intl.DateTimeFormat(language, { dateStyle: 'long', timeZone: 'UTC' }).format(date);
}

/** Public page for one legal document; rendered outside the sign-in gate. */
export function LegalDocumentPage({ legalDocument }: { legalDocument: LegalDocument }) {
  const { t, i18n } = useTranslation();
  const title = t(`legal.${legalDocument.id}`);

  return (
    <div className="h-full overflow-y-auto bg-background">
      <header className="border-b border-border">
        <div className="mx-auto flex w-full max-w-3xl items-center justify-between gap-4 px-6 py-4">
          <Link to="/" className="flex items-center gap-2 text-foreground" aria-label="RapidSOS Studio">
            <RapidSOSMark className="h-5 w-5" />
            <RapidSOSWordmark className="h-5" />
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            {t('legal.backToStudio')}
          </Link>
        </div>
      </header>

      {legalDocument.placeholder ? (
        <div
          role="note"
          className="border-b border-amber-500/30 bg-amber-500/10 px-6 py-2.5 text-sm text-amber-100"
        >
          <div className="mx-auto flex w-full max-w-3xl items-start gap-3">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" aria-hidden="true" />
            <p>{t('legal.draftBanner')}</p>
          </div>
        </div>
      ) : null}

      <main className="mx-auto w-full max-w-3xl px-6 py-10">
        <h1 className="text-3xl font-semibold tracking-tight text-foreground">{title}</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {t('legal.versionLine', {
            version: legalDocument.version,
            date: formatEffectiveDate(legalDocument.effectiveDate, i18n.resolvedLanguage ?? i18n.language),
          })}
        </p>
        <article
          lang="en"
          className="mt-8 text-sm leading-relaxed text-foreground/90 [&_a]:text-foreground [&_a]:underline [&_a]:underline-offset-2 [&_h2]:mb-3 [&_h2]:mt-10 [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:text-foreground [&_h2:first-child]:mt-0 [&_h3]:mb-2 [&_h3]:mt-6 [&_h3]:font-semibold [&_li]:my-1 [&_ol]:my-3 [&_ol]:ml-5 [&_ol]:list-decimal [&_p]:my-3 [&_strong]:font-semibold [&_strong]:text-foreground [&_ul]:my-3 [&_ul]:ml-5 [&_ul]:list-disc"
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{legalDocument.body}</ReactMarkdown>
        </article>
        <LegalLinks className="mt-12 border-t border-border pt-6" />
      </main>
    </div>
  );
}
